import { useRef } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useScroll,
} from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { AMAZON_URL } from "../lib/site";
import { scrollToSection } from "../lib/scroll";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1 -mb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const sectionRef = useRef(null);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), {
    stiffness: 120,
    damping: 16,
  });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), {
    stiffness: 120,
    damping: 16,
  });

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const bookY = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 140]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section
      id="top"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative overflow-hidden bg-[#F6F5F0] pt-[76px]"
    >
      {/* spotlight + texture */}
      <motion.div
        style={{ y: glowY }}
        className="pointer-events-none absolute -top-40 -left-40 h-[560px] w-[560px] rounded-full bg-[#20C9C3]/16 blur-[120px]"
      />
      <div className="pointer-events-none absolute top-24 right-[-160px] h-[460px] w-[460px] rounded-full bg-[#C5A253]/14 blur-[110px]" />
      <div className="grain pointer-events-none absolute inset-0 opacity-[0.5]" />
      <svg
        className="pointer-events-none absolute bottom-0 left-0 w-full text-[#C5A253]/25"
        viewBox="0 0 1440 140"
        fill="none"
        preserveAspectRatio="none"
      >
        {[0, 1, 2, 3].map((i) => (
          <path
            key={i}
            d={`M0 ${70 + i * 16} C 360 ${20 + i * 16}, 1080 ${120 + i * 16}, 1440 ${60 + i * 16}`}
            stroke="currentColor"
            strokeWidth="1"
          />
        ))}
      </svg>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid items-center gap-14 py-16 sm:py-20 lg:grid-cols-2 lg:gap-10 lg:py-24">
          {/* Book cover */}
          <motion.div
            style={{ y: bookY }}
            className="relative flex justify-center lg:justify-start lg:pl-6"
          >
            <motion.div
              initial={{ opacity: 0, y: 60, scale: 0.94 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 1.1, delay: 0.25, ease: EASE }}
              className="relative"
              onMouseMove={onMove}
              onMouseLeave={onLeave}
              style={{ perspective: 1100 }}
            >
              <div className="absolute -inset-6 rounded-[28px] border border-[#C5A253]/30" />
              <div className="absolute -top-3 -left-3 h-10 w-10 border-t-2 border-l-2 border-[#20C9C3]" />
              <div className="absolute -bottom-3 -right-3 h-10 w-10 border-b-2 border-r-2 border-[#C5A253]" />
              <motion.div
                animate={{ y: [0, -12, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              >
                <motion.img
                  src="/images/book-cover.png"
                  alt="50 Bookkeeping Red Flags Every Business Owner Should Know — book cover by Joede C. Brown, MSA"
                  data-testid="hero-book-cover"
                  style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
                  className="w-[290px] sm:w-[340px] lg:w-[380px] rounded-[4px] shadow-[0_50px_90px_-20px_rgba(30,42,43,0.45),0_25px_40px_-15px_rgba(32,201,195,0.25)]"
                />
              </motion.div>
              <div className="mx-auto mt-8 h-4 w-3/4 rounded-[50%] bg-[#1E2A2B]/18 blur-md" />
            </motion.div>
          </motion.div>

          {/* Copy */}
          <div className="relative text-center lg:text-left">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
              className="mb-6 flex items-center justify-center lg:justify-start gap-3"
            >
              <span className="h-px w-10 bg-[#C5A253]" />
              <span
                data-testid="hero-eyebrow"
                className="text-[12px] sm:text-[13px] font-bold uppercase tracking-[0.24em] text-[#14A8A3]"
              >
                A Practical Guide for Business Owners
              </span>
            </motion.div>

            <h1
              data-testid="hero-heading"
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.06] tracking-tight text-[#1E2A2B]"
            >
              <MaskedLine delay={0.3}>50 Bookkeeping</MaskedLine>
              <MaskedLine delay={0.42}>
                <span className="relative inline-block italic text-[#14A8A3]">
                  Red Flags
                  <motion.svg
                    className="absolute -bottom-2 left-0 w-full"
                    viewBox="0 0 220 14"
                    fill="none"
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 1 }}
                    transition={{ duration: 0.9, delay: 1.15, ease: "easeOut" }}
                  >
                    <motion.path
                      d="M4 9 C 60 3, 150 12, 216 6"
                      stroke="#C5A253"
                      strokeWidth="4"
                      strokeLinecap="round"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 0.9, delay: 1.15, ease: "easeOut" }}
                    />
                  </motion.svg>
                </span>
              </MaskedLine>
              <MaskedLine delay={0.54}>Every Business Owner</MaskedLine>
              <MaskedLine delay={0.66}>Should Know</MaskedLine>
            </h1>

            <motion.p
              data-testid="hero-subtext"
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
              className="mx-auto lg:mx-0 mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-[#1E2A2B]/70"
            >
              Your books may be telling you something is wrong before you
              realize there is a problem. Learn what to look for, why it
              matters and what steps you can take next.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
              className="mt-5 font-display text-lg italic text-[#1E2A2B]/85"
              data-testid="hero-author-byline"
            >
              By <span className="not-italic font-semibold text-[#1E2A2B]">Joede Brown, MSA</span>
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
              className="mt-9 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-5"
            >
              <a
                href={AMAZON_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-buy-button"
                className="group inline-flex items-center gap-2.5 rounded-full bg-[#20C9C3] px-9 py-4 text-sm font-bold uppercase tracking-[0.12em] text-white shadow-[0_16px_38px_rgba(32,201,195,0.4)] transition-[background-color,transform,box-shadow] duration-300 hover:bg-[#14A8A3] hover:-translate-y-1 hover:shadow-[0_22px_46px_rgba(32,201,195,0.5)]"
              >
                Buy the Book on Amazon
                <ArrowUpRight className="h-[18px] w-[18px] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
              <a
                href="#inside"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection("#inside");
                }}
                data-testid="hero-explore-link"
                className="group inline-flex items-center gap-2 text-[13px] font-bold uppercase tracking-[0.16em] text-[#1E2A2B]/70 hover:text-[#14A8A3] transition-colors duration-300"
              >
                See what&rsquo;s inside
                <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
              </a>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
